import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isDesktopApp } from './platform';
import { useImportPackage } from './chants';

/**
 * Opening a file the OS handed to the app.
 *
 * The desktop shell registers itself for `.vuchant` and knows how to say which
 * files it was launched with — but a registration nothing listens to is worse
 * than no registration at all: double-clicking a package would open the window
 * and then appear to do nothing, which reads as a broken app rather than a
 * missing feature.
 *
 * Two ways a file arrives, and both end here:
 *
 *   - the app was LAUNCHED with it (`launch_files`), asked for once the page
 *     has mounted rather than pushed at a window that may not exist yet;
 *   - the app was ALREADY RUNNING and a second launch handed its arguments
 *     over (`vu://open-files`), because two windows over one document is a
 *     lost-edit machine.
 *
 * The package goes through the SAME endpoint the browser's upload button uses,
 * so a file opened from the desktop and a file dragged into the web app produce
 * the same document by the same code — including the manifest-hash check that
 * refuses one edited outside the tools.
 *
 * A no-op in a browser, so `App` can mount it unconditionally.
 */
export interface OpenedFileState {
  /** What is being opened right now, for a status line. */
  busy: string | null;
  error: string | null;
  dismiss: () => void;
}

export function useOpenedFiles(): OpenedFileState {
  const navigate = useNavigate();
  const importPkg = useImportPackage();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDesktopApp) return;
    let live = true;

    const open = async (files: { path: string; name: string }[]): Promise<void> => {
      const first = files[0];
      if (first === undefined) return;
      // One at a time, and only the first: opening five documents at once would
      // navigate five times and leave the author wherever the race ended.
      setBusy(first.name);
      setError(null);
      try {
        const { readFile } = await import('./desktop');
        const bytes = await readFile(first.path);
        if (!/\.vuchant$/i.test(first.name)) {
          throw new Error(
            `${first.name} is not a chant package. Word and PDF are imported from`
            + ' the chants list, where the report can be read.',
          );
        }
        const file = new File([bytes as BlobPart], first.name, {
          type: 'application/vnd.vedaunion.chant+zip',
        });
        const r = await importPkg.mutateAsync(file);
        if (!live) return;
        navigate(`/dashboard/chants/${r.chant.slug}`);
      } catch (e) {
        // NOT SIGNED IN is the ordinary case, not an edge one: the app can be
        // launched by double-clicking a file long before anyone has signed in,
        // and it opens on the sign-in page with this banner over it. Saying
        // "import failed" there would send someone looking for a fault in a
        // document that is perfectly fine.
        //
        // 401 and 403 both mean it here, and the 403 is the CSRF middleware
        // doing its job — an unauthenticated cross-origin POST is exactly what
        // it exists to refuse. Once signed in the bearer header skips it.
        const status = (e as { status?: number }).status;
        if (live) {
          setError(
            status === 401 || status === 403
              ? `Sign in, then open ${first.name} again.`
              : e instanceof Error ? e.message : `Could not open ${first.name}.`,
          );
        }
      } finally {
        if (live) setBusy(null);
      }
    };

    let stop: (() => void) | null = null;

    void (async () => {
      const { launchFiles, onOpenFiles } = await import('./desktop');
      const at = await launchFiles();
      if (live && at.length > 0) await open(at);
      const off = await onOpenFiles((files) => { void open(files); });
      // The subscription resolves asynchronously, so unmounting first is a real
      // race: unsubscribe immediately rather than leaving a listener behind.
      if (!live) off();
      else stop = off;
    })();

    return () => {
      live = false;
      stop?.();
    };
    // Mounted once for the life of the app: re-subscribing on every render
    // would leave listeners behind and open a dropped file several times.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { busy, error, dismiss: () => setError(null) };
}
